import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ApiError } from '../../identity/types';
import escochexLogo from '../../../assets/escochex-logo.webp';
import { reportsGateway } from '../index';
import { formatReportCurrency, formatReportDate } from '../formatting';
import { PAYSLIP_STATUS_NOTE_PREFIX, payslipStatusView } from '../payslipStatus';
import { PayslipAvailabilityNotices } from '../PayslipNotices';
import { formatEventInstant } from '../timeZone';
import { TimeZoneIndicator, TimeZoneSelect } from '../TimeZoneControl';
import { useTimeZoneChoice } from '../useTimeZoneChoice';
import { PayslipStatusBadge } from '../PayslipStatusBadge';
import { MailIcon, PrintIcon, UnavailableAction } from '../UnavailableAction';
import type { EmployeePayslipResponse } from '../types';
import '../payslips.css';

function maskedSsn(ssnLast4: string | null): string | null {
  return ssnLast4 ? `XXX-XX-${ssnLast4}` : null;
}

/** Employee Payslip Preview (EPN-73, extended by EPN-236 for Figma S02-S04 and EPN-237 for real status) — the "Earning
 * Statement" detail page, backed by `ReportsGateway.getEmployeePayslip`. One document structure for every status; only
 * the status badge, the dated status note, the Voided caution banner and the three VOIDED watermarks differ.
 *
 * It deliberately omits what the approved Figma shows but no backend source exists for: per-earning-line breakdown,
 * an employer-benefit-contribution breakdown and employee/company address and phone — see
 * `docs/epn-73-payslip-gaps.md` and `docs/employee-payslips-admin.md`. The endpoint returns payslips of Approved runs
 * only (404 otherwise); since EPN-237 the status is derived server-side (Approved, Processing, Paid, Voided), a Paid
 * from a mock provider reads "Paid (simulated)", and a failed payment keeps the payslip Approved with a notice. When the
 * caller cannot read payment facts or reversals, the response says so and a notice explains why the status may be
 * Approved. An unrecognised status degrades to a neutral chip with the raw label, never to Paid. The dated status note
 * renders only when the response carries `statusSince`.
 *
 * "Print" is the browser's own print dialog (`window.print()`): a local output convenience only, not audit-logged
 * (see docs/epn-73-payslip-gaps.md section 4). Email is shown disabled with a reason; Download PDF is not offered. */
export function EmployeePayslipPreviewPage() {
  const { payrollRunId, employeeId } = useParams<{ payrollRunId: string; employeeId: string }>();
  const navigate = useNavigate();
  const [payslip, setPayslip] = useState<EmployeePayslipResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { choice: zoneChoice, setChoice: setZoneChoice, resolved: zone } = useTimeZoneChoice();

  useEffect(() => {
    if (!payrollRunId || !employeeId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setNotFound(false);
    setError(null);
    setPayslip(null);

    reportsGateway
      .getEmployeePayslip(payrollRunId, employeeId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setPayslip(result);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        setError(friendlyErrorMessage(err, 'Could not load this payslip.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [payrollRunId, employeeId, reloadToken]);

  // Keyboard/screen-reader users arrive from the list: land on the page heading once the outcome is known.
  useEffect(() => {
    if (!loading) headingRef.current?.focus();
  }, [loading]);

  const hasGarnishments = payslip !== null && (payslip.garnishmentsTotal > 0 || payslip.ytd.garnishmentsTotal > 0);
  const statusView = payslip ? payslipStatusView(payslip.status) : null;
  const isVoided = statusView?.status === 'Voided';
  // "Paid on Feb 16, 2026, 3:42 PM PST": the event instant in the viewer's chosen zone. The Pay Date below is a plain calendar date and is never shifted.
  const statusWhen = statusView?.status ? formatEventInstant(payslip?.statusSince, zone.zone) : null;
  const statusNote = statusView?.status && statusWhen ? `${PAYSLIP_STATUS_NOTE_PREFIX[statusView.status]} ${statusWhen}` : null;
  const ytdExcludesVoided = payslip?.reversalsAvailable !== false;
  const simulatedPaid = statusView?.status === 'Paid' && payslip?.simulated === true;

  return (
    <section className="card card--wide payslips payslip-preview" aria-labelledby="payslip-preview-heading" aria-busy={loading}>
      <div className="section-header no-print">
        <h2 id="payslip-preview-heading" ref={headingRef} tabIndex={-1}>Payslip Preview</h2>
        <div className="payslips__actions">
          <button type="button" className="payslips__header-btn" onClick={() => navigate('/payroll/employee-payslips')}>Back to payslip list</button>
          {payslip && (
            <UnavailableAction label="Email payslip" reason="Emailing a payslip is not available yet" className="payslips__header-btn">
              <MailIcon /> Email
            </UnavailableAction>
          )}
          {payslip && <button type="button" className="payslips__header-btn payslips__header-btn--primary" onClick={() => window.print()}><PrintIcon /> Print</button>}
        </div>
      </div>
      {payslip && <TimeZoneSelect choice={zoneChoice} onChange={setZoneChoice} />}

      {loading && <p className="muted" role="status">Loading payslip…</p>}
      {error && (
        <div className="payslips__error no-print" role="alert">
          <p className="error">{error}</p>
          <button type="button" onClick={() => setReloadToken((n) => n + 1)}>Try again</button>
        </div>
      )}
      {notFound && (
        <div className="payslips__empty no-print" role="alert">
          <p><strong>This payslip is not available.</strong></p>
          <p className="muted">Payslips are only available for approved payroll runs. Return to the payslip list to choose another.</p>
        </div>
      )}

      {payslip && (
        <PayslipAvailabilityNotices
          paymentStatusAvailable={payslip.paymentStatusAvailable !== false}
          reversalsAvailable={payslip.reversalsAvailable !== false}
          paymentFailedCount={payslip.paymentFailed ? 1 : 0}
          subject="payslip"
        />
      )}

      {payslip && (
        <article className={`earning-statement${isVoided ? ' earning-statement--voided' : ''}`} data-status={statusView?.status ?? 'unknown'}>
          {isVoided && (
            <>
              {/* Three decorative impressions, centre noticeably larger. Real DOM text (not an overlay image) with
                  print-color-adjust in the print stylesheet, so they survive print / save-as-PDF. */}
              <span className="earning-statement__watermark earning-statement__watermark--top" aria-hidden="true">VOIDED</span>
              <span className="earning-statement__watermark earning-statement__watermark--center" aria-hidden="true">VOIDED</span>
              <span className="earning-statement__watermark earning-statement__watermark--bottom" aria-hidden="true">VOIDED</span>
              <p className="earning-statement__void-banner" role="note">
                This payslip has been voided and should not be relied on as an active pay record. The original figures are kept for reference.
                {ytdExcludesVoided && ' Year-to-date totals do not include this voided payslip.'}
              </p>
            </>
          )}
          <header className="earning-statement__header">
            <div className="earning-statement__title">
              <img className="earning-statement__logo" src={escochexLogo} alt="Escochex" />
              <h3>Earning Statement</h3>
            </div>
            <div className="earning-statement__status">
              <PayslipStatusBadge status={payslip.status} simulated={payslip.simulated === true} variant="statement" />
              {statusNote && <p className="earning-statement__status-note">{statusNote}</p>}
              <TimeZoneIndicator choice={zoneChoice} className="earning-statement__tz" />
              {simulatedPaid && <p className="earning-statement__status-note">Simulated payment: no real funds were moved.</p>}
            </div>
          </header>

          <dl aria-label="Pay period and check date" className="earning-statement__meta">
            <div><dt>Pay Period</dt><dd>{formatReportDate(payslip.periodStartDate)} – {formatReportDate(payslip.periodEndDate)}</dd></div>
            <div><dt>Pay Date</dt><dd>{formatReportDate(payslip.checkDate)}</dd></div>
            {payslip.memo?.trim() && <div><dt>Memo</dt><dd>{payslip.memo}</dd></div>}
          </dl>

          <div className="earning-statement__parties">
            <div>
              <h4>Company</h4>
              <p>{payslip.companyName ?? 'Not available'}</p>
            </div>
            <div>
              <h4>Employee</h4>
              <p>{payslip.employeeName}</p>
              <p>ID: {payslip.employeeNumber}{maskedSsn(payslip.ssnLast4) ? ` · SSN: ${maskedSsn(payslip.ssnLast4)}` : ''}</p>
            </div>
          </div>

          <section aria-labelledby="earnings-heading">
            <h4 id="earnings-heading">Employee Earnings</h4>
            <div className="table-scroll">
              <table aria-label="Employee earnings">
                <thead><tr><th scope="col">Description</th><th scope="col" className="num">Current</th><th scope="col" className="num">Year to Date</th></tr></thead>
                <tbody>
                  <tr><th scope="row">Gross Earnings</th><td className="num">{formatReportCurrency(payslip.grossEarnings)}</td><td className="num">{formatReportCurrency(payslip.ytd.grossEarnings)}</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          {payslip.leave.length > 0 && (
            <section aria-labelledby="leave-heading">
              <h4 id="leave-heading">Employee Leaves</h4>
              <div className="table-scroll">
                <table aria-label="Employee leave balances">
                  <thead><tr><th scope="col">Leave Type</th><th scope="col" className="num">Used</th><th scope="col" className="num">Accrued</th><th scope="col" className="num">Available</th></tr></thead>
                  <tbody>
                    {payslip.leave.map((leave) => (
                      <tr key={leave.leavePlanName}>
                        <th scope="row">{leave.leavePlanName}</th>
                        <td className="num">{leave.used.toFixed(2)}</td>
                        <td className="num">{leave.accrued.toFixed(2)}</td>
                        <td className="num">{leave.available.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <div className="earning-statement__columns">
            <section aria-labelledby="deductions-heading">
              <h4 id="deductions-heading">Employee Deductions</h4>
              <div className="table-scroll">
                <table aria-label="Employee deductions">
                  <thead><tr><th scope="col">Description</th><th scope="col" className="num">Current</th><th scope="col" className="num">Year to Date</th></tr></thead>
                  <tbody>
                    <tr><th scope="row">Pre-Tax Deductions</th><td className="num">{formatReportCurrency(payslip.preTaxDeductionsTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.preTaxDeductionsTotal)}</td></tr>
                    <tr><th scope="row">Post-Tax Deductions</th><td className="num">{formatReportCurrency(payslip.postTaxDeductionsTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.postTaxDeductionsTotal)}</td></tr>
                    {hasGarnishments && (
                      <tr><th scope="row">Garnishments</th><td className="num">{formatReportCurrency(payslip.garnishmentsTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.garnishmentsTotal)}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section aria-labelledby="employer-taxes-heading">
              <h4 id="employer-taxes-heading">Employer Taxes</h4>
              <div className="table-scroll">
                <table aria-label="Employer taxes">
                  <thead><tr><th scope="col">Description</th><th scope="col" className="num">Current</th></tr></thead>
                  <tbody>
                    {payslip.employerTaxLines.map((line) => (
                      <tr key={line.description}><th scope="row">{line.description}</th><td className="num">{formatReportCurrency(line.amount)}</td></tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><th scope="row">Total Employer Taxes</th><td className="num">{formatReportCurrency(payslip.employerTaxesTotal)}</td></tr>
                  </tfoot>
                </table>
              </div>
            </section>
          </div>

          <section aria-labelledby="employee-taxes-heading">
            <h4 id="employee-taxes-heading">Employee Taxes Withheld</h4>
            <div className="table-scroll">
              <table aria-label="Employee taxes withheld">
                <thead><tr><th scope="col">Tax</th><th scope="col" className="num">Current</th></tr></thead>
                <tbody>
                  {payslip.employeeTaxLines.map((line) => (
                    <tr key={line.description}><th scope="row">{line.description}</th><td className="num">{formatReportCurrency(line.amount)}</td></tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><th scope="row">Total Taxes Withheld</th><td className="num">{formatReportCurrency(payslip.employeeTaxesTotal)}</td></tr>
                </tfoot>
              </table>
            </div>
          </section>

          <section aria-labelledby="summary-heading" className="earning-statement__summary">
            <h4 id="summary-heading">Summary</h4>
            <div className="table-scroll">
              <table aria-label="Payslip summary">
                <thead><tr><th scope="col">Description</th><th scope="col" className="num">Current</th><th scope="col" className="num">Year to Date</th></tr></thead>
                <tbody>
                  <tr><th scope="row">Gross Earnings</th><td className="num">{formatReportCurrency(payslip.grossEarnings)}</td><td className="num">{formatReportCurrency(payslip.ytd.grossEarnings)}</td></tr>
                  <tr><th scope="row">Employee Taxes</th><td className="num">{formatReportCurrency(payslip.employeeTaxesTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.employeeTaxes)}</td></tr>
                  <tr><th scope="row">Pre-Tax Deductions</th><td className="num">{formatReportCurrency(payslip.preTaxDeductionsTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.preTaxDeductionsTotal)}</td></tr>
                  <tr><th scope="row">Post-Tax Deductions</th><td className="num">{formatReportCurrency(payslip.postTaxDeductionsTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.postTaxDeductionsTotal)}</td></tr>
                  {hasGarnishments && (
                    <tr><th scope="row">Garnishments</th><td className="num">{formatReportCurrency(payslip.garnishmentsTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.garnishmentsTotal)}</td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr><th scope="row">Net Pay</th><td className="num">{formatReportCurrency(payslip.netPay)}</td><td className="num">{formatReportCurrency(payslip.ytd.netPay)}</td></tr>
                </tfoot>
              </table>
            </div>
            <p className="muted earning-statement__note">
              Net Pay is Gross Earnings less the employee taxes and deductions listed above.
            </p>

            <h4 id="employer-cost-heading">Employer Taxes (employer cost)</h4>
            <div className="table-scroll">
              <table aria-label="Employer taxes, not deducted from net pay">
                <thead><tr><th scope="col">Description</th><th scope="col" className="num">Current</th><th scope="col" className="num">Year to Date</th></tr></thead>
                <tbody>
                  <tr><th scope="row">Employer Taxes</th><td className="num">{formatReportCurrency(payslip.employerTaxesTotal)}</td><td className="num">{formatReportCurrency(payslip.ytd.employerTaxes)}</td></tr>
                </tbody>
              </table>
            </div>
            <p className="muted earning-statement__note">Employer taxes are an employer cost and are not deducted from Net Pay.</p>
          </section>
        </article>
      )}
    </section>
  );
}
